import { pendingSideLabel, playoffSeedInfoByParticipant, type PlayoffSeedInfo, type TournamentMatch } from "./api";
import type { BracketCompetitor, BracketMatch, BracketRound } from "./bracketLayout";
import { DEAD_SIDE_LABEL, deadBracketSides, isDeadSide } from "./bracketDeadSide";
import {
  eliminationRoundDisplayLabel,
  isCancelledPlayoffMatch,
  isStructuralPlayoffMatch,
  knockoutStageName,
  parsePlayoffJornadaNumber,
  playoffStageSequenceForRounds,
} from "./roundRobinPhases";

/**
 * Build the presentation-agnostic knockout bracket (rounds + optional third
 * place) from a plain list of tournament matches — no admin-only context
 * (groups, configs, edits) required. Used by the player-facing bracket view.
 *
 * Handles the three round-label dialects that exist in production data:
 * - English elimination keys stored by the backend ("Round of 32", "Semifinals").
 * - Legacy Spanish playoff labels ("Dieciseisavos de final", "Cuartos de final").
 * - Generated phase playoffs ("Jornada N" + "Playoff v2" in notes, group_id null).
 */

function normalizeLabel(label: string | null | undefined): string {
  return (label ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

/** Cuadro de consolación (2026-10-17): rondas «C · 1ª ronda», «C · Semis»,
 * «C · Final», «C · 3er lugar». Nunca son columnas del cuadro principal. */
export const CONSOLATION_PREFIX = "C · ";

export function isConsolationLabel(label: string | null | undefined): boolean {
  return (label ?? "").startsWith(CONSOLATION_PREFIX);
}

const CONSOLATION_ROUND_ORDER = ["1ª ronda", "64avos", "32avos", "16avos", "Octavos", "Cuartos", "Semis", "Final"];

/** Orden de columna dentro del cuadro de consolación; null = 3er lugar u otra cosa. */
export function consolationRoundOrder(label: string | null | undefined): number | null {
  if (!isConsolationLabel(label)) return null;
  const index = CONSOLATION_ROUND_ORDER.indexOf((label ?? "").slice(CONSOLATION_PREFIX.length).trim());
  return index >= 0 ? index : null;
}

/** "3er Lugar" / "Tercer lugar" / consolación — a side match, never a column. */
export function isThirdPlaceLabel(label: string | null | undefined): boolean {
  if (isConsolationLabel(label)) return false;
  const normalized = normalizeLabel(label);
  return normalized.includes("tercer") || normalized.startsWith("3er") || normalized.includes("consolacion");
}

/** Canonical column order for a knockout stage label; null = not a stage label. */
export function knockoutStageOrder(label: string | null | undefined): number | null {
  const normalized = normalizeLabel(label);
  if (!normalized || isThirdPlaceLabel(label) || isConsolationLabel(label)) return null;
  if (normalized === "preliminar") return 0;
  if (normalized.includes("de 256") || normalized.includes("of 256")) return 1;
  if (normalized.includes("de 128") || normalized.includes("of 128")) return 2;
  if (normalized.includes("de 64") || normalized.includes("of 64")) return 3;
  if (normalized.includes("dieciseisavos") || normalized.includes("de 32") || normalized.includes("of 32")) return 4;
  if (normalized.includes("octavos") || normalized.includes("de 16") || normalized.includes("of 16")) return 5;
  if (normalized.includes("cuartos") || normalized.includes("quarterfinal")) return 6;
  if (normalized.includes("semifinal")) return 7;
  if (normalized === "final" || /(^|\s)final(\s|$)/.test(normalized)) return 8;
  return null;
}

/** Eduardo 2026-09-16: en el cuadro, el que se retiró lleva "RET" junto a
 * sus sets; el descalificado, "DEF" (Excepción «Default (DEF)», Eduardo
 * 2026-10-13). W/O se conserva para partidos ya capturados así. */
export function loserTag(status: string | null | undefined): string | null {
  if (status === "retired") return "RET";
  if (status === "walkover" || status === "no_show") return "W/O";
  if (status === "defaulted") return "DEF";
  return null;
}

export const DROPPED_TAG = "BAJA";

/** Etiqueta para un lado concreto del partido: solo el perdedor la lleva.
 * La usan también los brackets que arma el Draw admin por su cuenta
 * (smoke de Hermes 2026-09-16: ahí faltaba el RET). */
export function competitorLoserTag(match: TournamentMatch, participantId: number | null | undefined): string | null {
  if (participantId == null) return null;
  if (match.winner_participant_id === participantId) return null;
  if (match.status === "double_walkover") return "W/O";
  const tag = match.winner_participant_id != null ? loserTag(match.status) : null;
  if (tag) return tag;
  // Eduardo 2026-09-18 (torneo 49): un jugador dado de baja seguía en el
  // cuadro sin ninguna seña y parecía de otra categoría → lleva "BAJA".
  const brief =
    match.player1_participant_id === participantId ? match.player1
    : match.player2_participant_id === participantId ? match.player2
    : null;
  return brief?.current_status === "dropped" ? DROPPED_TAG : null;
}


type SeedLookup = Map<number, PlayoffSeedInfo>;

function competitorFor(match: TournamentMatch, side: "p1" | "p2", seeds?: SeedLookup): BracketCompetitor | null {
  const participantId = side === "p1" ? match.player1_participant_id : match.player2_participant_id;
  const brief = side === "p1" ? match.player1 : match.player2;
  if (!brief && participantId === null) return null;
  const isWinner = match.winner_participant_id != null && match.winner_participant_id === participantId;
  return {
    // short_name primero: el display_name de una pareja es el team.name
    // crudo y truncado en el globo no identifica a nadie.
    name: brief?.short_name?.trim() || brief?.display_name?.trim() || (participantId ? `#${participantId}` : pendingSideLabel(match.notes, side === "p1" ? 1 : 2)),
    // Misma semántica que el draw admin: el seed oficial/inicial manda;
    // tournament_rank es el alias legado y current_rank solo el fallback vivo.
    // Playoffs desde grupos (2026-10-20): si el cuadro guardó seeds de
    // playoff, manda ESE seed (no el ranking de inscripción). Sin marcadores
    // (cuadros viejos / eliminatoria) queda el comportamiento anterior.
    seed: seeds && seeds.size > 0
      ? (participantId != null ? seeds.get(participantId)?.seed ?? null : null)
      : brief?.initial_rank ?? brief?.tournament_rank ?? brief?.current_rank ?? null,
    games: (match.score_sets ?? []).map((set) => (side === "p1" ? set.player1_games : set.player2_games)),
    isWinner,
    tag: competitorLoserTag(match, participantId),
  };
}

function toBracketMatch(match: TournamentMatch, keyPrefix: string, seeds?: SeedLookup, dead?: Set<string>): BracketMatch {
  const top = competitorFor(match, "p1", seeds);
  const bottom = competitorFor(match, "p2", seeds);
  return {
    id: `${keyPrefix}-${match.id}`,
    matchId: match.id,
    number: match.match_number ?? null,
    top,
    bottom,
    ...(top === null && dead && isDeadSide(dead, match, 1) ? { topSource: DEAD_SIDE_LABEL } : {}),
    ...(bottom === null && dead && isDeadSide(dead, match, 2) ? { bottomSource: DEAD_SIDE_LABEL } : {}),
    pending: !match.player1_participant_id || !match.player2_participant_id,
  };
}

function sortByNumber(a: TournamentMatch, b: TournamentMatch): number {
  const aNumber = a.match_number ?? 1_000_000;
  const bNumber = b.match_number ?? 1_000_000;
  return aNumber - bNumber || a.id - b.id;
}

export interface KnockoutBracketData {
  rounds: BracketRound[];
  thirdPlace: BracketMatch | null;
}

function isPowerOfTwo(value: number): boolean {
  return value > 0 && (value & (value - 1)) === 0;
}

/** Completa el árbol con rondas placeholder (mitades sucesivas hasta la
 * Final) cuando el cuadro solo tiene sus primeras rondas generadas. Solo
 * proyecta desde rondas completas (potencia de 2): las rondas parciales con
 * descansos las materializa el organizador con su propia numeración. */
export function withProjectedFutureRounds(rounds: BracketRound[]): BracketRound[] {
  if (rounds.length === 0) return rounds;
  const projected = [...rounds];
  let previousCount = projected[projected.length - 1].matches.length;
  if (!isPowerOfTwo(previousCount)) return projected;
  while (previousCount > 1) {
    const nextCount = previousCount / 2;
    projected.push({
      label: knockoutStageName(nextCount),
      matches: Array.from({ length: nextCount }, (_, index) => ({
        id: `proj-${nextCount}-${index + 1}`,
        matchId: null,
        top: null,
        bottom: null,
        topSource: `G M${index * 2 + 1}`,
        bottomSource: `G M${index * 2 + 2}`,
        pending: true,
      })),
    });
    previousCount = nextCount;
  }
  return projected;
}

export function buildKnockoutBracket(matches: TournamentMatch[]): KnockoutBracketData {
  const seeds = playoffSeedInfoByParticipant(matches);
  const dead = deadBracketSides(matches);
  const staged = new Map<number, { rawLabel: string | null; matches: TournamentMatch[] }>();
  const jornadas = new Map<number, TournamentMatch[]>();
  let thirdMatch: TournamentMatch | null = null;

  for (const match of matches) {
    // Cancelados FUERA antes de derivar etapas y de armar columnas: una Final
    // cancelada + regenerada dejaría dos filas en la jornada final, el árbol
    // parecería incompleto y la Final activa se mostraría como "Semifinal"
    // (mismo criterio que el backend, `playoff_labels.py`).
    if (isCancelledPlayoffMatch(match)) continue;
    if (isThirdPlaceLabel(match.round_label)) {
      thirdMatch = thirdMatch ?? match;
      continue;
    }
    const order = knockoutStageOrder(match.round_label);
    if (order !== null) {
      const bucket = staged.get(order) ?? { rawLabel: match.round_label, matches: [] };
      bucket.matches.push(match);
      staged.set(order, bucket);
      continue;
    }
    // Playoffs de fase generada: conservan "Jornada N" y el marcador vive en
    // notes. Predicado COMPARTIDO con roundRobinPhases (v2 y v1 legado).
    if (isStructuralPlayoffMatch(match)) {
      const jornada = parsePlayoffJornadaNumber(match.round_label);
      if (jornada !== null) {
        const list = jornadas.get(jornada) ?? [];
        list.push(match);
        jornadas.set(jornada, list);
      }
    }
  }

  let rounds: BracketRound[];
  if (staged.size > 0) {
    rounds = [...staged.entries()]
      .sort(([a], [b]) => a - b)
      .map(([, bucket]) => ({
        label: eliminationRoundDisplayLabel(bucket.rawLabel),
        matches: [...bucket.matches].sort(sortByNumber).map((match) => toBracketMatch(match, "ko", seeds, dead)),
      }));
    // Proyectar las rondas FUTURAS aunque el organizador aún no las genere,
    // igual que la vista admin (feedback Eduardo 2026-07-15: un cuadro de 64
    // recién sorteado mostraba una sola columna al jugador). Cada slot indica
    // de qué match viene ("G M1" = ganador del match 1 de la ronda previa).
    rounds = withProjectedFutureRounds(rounds);
  } else {
    // Etiquetas canónicas derivadas de la ESTRUCTURA (Preliminar por notes,
    // Final hacia atrás), no del texto "Jornada N" — ver roundRobinPhases.
    const orderedJornadas = [...jornadas.entries()].sort(([a], [b]) => a - b);
    const stageLabels = playoffStageSequenceForRounds(orderedJornadas.map(([, list]) => list));
    rounds = orderedJornadas.map(([jornada, list], index) => ({
      // Sin etiqueta confiable (conteo que no es potencia de 2) se muestra la
      // etiqueta CRUDA, igual que el backend: nunca una "Ronda de 6".
      label:
        stageLabels[index]
        ?? eliminationRoundDisplayLabel(list[0]?.round_label ?? `Jornada ${jornada}`),
      matches: [...list].sort(sortByNumber).map((match) => toBracketMatch(match, "ko", seeds, dead)),
    }));
  }

  return {
    rounds,
    thirdPlace: thirdMatch ? toBracketMatch(thirdMatch, "third", seeds, dead) : null,
  };
}

/** Cuadro de consolación del alcance: columnas «C · …» en orden y el
 * «C · 3er lugar» como partido aparte. Vacío si no hay consolación. */
export function buildConsolationBracket(matches: TournamentMatch[]): KnockoutBracketData {
  const dead = deadBracketSides(matches);
  const byOrder = new Map<number, { label: string; matches: TournamentMatch[] }>();
  let thirdMatch: TournamentMatch | null = null;
  for (const match of matches) {
    if (!isConsolationLabel(match.round_label) || String(match.status) === "cancelled") continue;
    const order = consolationRoundOrder(match.round_label);
    if (order === null) {
      thirdMatch = thirdMatch ?? match;
      continue;
    }
    const bucket = byOrder.get(order) ?? { label: match.round_label ?? "", matches: [] };
    bucket.matches.push(match);
    byOrder.set(order, bucket);
  }
  const rounds: BracketRound[] = [...byOrder.entries()]
    .sort(([a], [b]) => a - b)
    .map(([, bucket]) => ({
      label: bucket.label,
      matches: [...bucket.matches].sort(sortByNumber).map((match) => toBracketMatch(match, "c", undefined, dead)),
    }));
  return { rounds, thirdPlace: thirdMatch ? toBracketMatch(thirdMatch, "c3", undefined, dead) : null };
}
